/**
 * GET `/roster/issued_roster/me` — 로그인 사용자(nurse_id) 한 명에 대해,
 * 그룹의 활성 발행 스냅샷 `roster_json`에서 본인 행만 잘라서 반환.
 *
 * - 성공 **200**: {@link MyIssuedRosterResponse}
 * - **404** (`발행된 근무표가 없습니다.`) → `data: null` (쿼리는 error 아님)
 * - **401** 등 → throw (React Query `isError`)
 * - 쿼리: `year`, `month` 필수. `group_id` 없음(세션 그룹 스냅샷만).
 */

import useStore from "@store/store";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { RosterScheduleDayCell } from "@vTypes/snapshot-type";

export type MyIssuedRosterTransfer = {
  reason: string;
  target_group_id: string;
  target_group_name: string;
  start_date: string;
  end_date: string | null;
  period_start_day: number;
  period_end_day: number;
  target_issued: boolean;
};

export type MyIssuedRosterGroup = {
  snapshot_id?: number | null;
  group_id: string;
  group_name: string;
};

/** 신규 `/me` 일정 원소(일자별 상세 메타 포함) */
export type MyIssuedRosterScheduleEntry = RosterScheduleDayCell & {
  day: number;
  code: string;
  color: string;
  schedule_id: number | null;
  group_id: string;
  group_name: string;
  is_source: boolean;
  reason: string | null;
};

/** GET /roster/issued_roster/me 성공 본문 */
export type MyIssuedRosterResponse = {
  snapshot_id?: number | null;
  year: number | null;
  month: number | null;
  nurse_id: string;
  name: string;
  home_group_id?: string;
  home_group_name?: string;
  source_group_id?: string;
  source_group_name?: string;
  /**
   * 1일~말일 스케줄.
   * - 신규: {@link MyIssuedRosterScheduleEntry}[]
   * - 구버전 호환: 문자열 또는 `{ code, color }`
   */
  schedule: Array<string | RosterScheduleDayCell | MyIssuedRosterScheduleEntry>;
  /** 구버전 필드(신규 스펙에서는 `schedule[].schedule_id`로 대체 가능) */
  schedule_ids?: (number | null)[];
  counts: Record<string, number>;
  ward_counts?: Array<{
    group_id: string;
    group_name: string;
    shifts: Array<{
      code: string;
      name: string;
      color: string | null;
      count: number;
      schedule_id?: number | null;
    }>;
  }> | null;
  shift_colors: Record<string, string>;
  /** 스냅샷 `created_at` (ISO 8601) */
  issued_at: string | null;
  /** 기간 배정(파견/병동이동/휴직 등) 메타. 없을 수 있음 — 구버전 단일 필드 */
  transfer?: MyIssuedRosterTransfer | null;
  /** 기간 배정 목록(우선). 없으면 {@link transfer} 단일 값으로 대체 가능 */
  transfers?: MyIssuedRosterTransfer[] | null;
  /** 신규: 사용된 병동 목록 */
  groups?: MyIssuedRosterGroup[] | null;
};

export default function useMyIssuedRoster({
  year,
  month,
}: {
  year?: number | string;
  month?: number | string;
} = {}) {
  const { loginUser } = useStore();

  const y = year != null && year !== "" ? Number(year) : undefined;
  const m = month != null && month !== "" ? Number(month) : undefined;

  return useQuery<MyIssuedRosterResponse | null>({
    queryKey:
      y != null && m != null && !Number.isNaN(y) && !Number.isNaN(m)
        ? [
            ...QUERY_KEYS.roster.myIssuedRoster(y, m),
            loginUser?.account_id,
            loginUser?.office_id,
          ]
        : ["ROSTER_ISSUED_ROSTER_ME", "disabled"],
    queryFn: async ({ signal }) => {
      const yearNum = y as number;
      const monthNum = m as number;
      try {
        return await httpClient.get<MyIssuedRosterResponse>(
          "/roster/issued_roster/me",
          {
            params: { year: yearNum, month: monthNum },
            skipGroupIdInjection: true,
            signal,
          }
        );
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          return null;
        }
        throw e;
      }
    },
    enabled:
      !!loginUser &&
      y != null &&
      m != null &&
      !Number.isNaN(y) &&
      !Number.isNaN(m),
    staleTime: 5 * 60 * 1000,
  });
}
